import { useEffect, useMemo, useRef, useState } from 'react';

/* eslint frontbucket-patterns/no-new-emotion-styled: "warn" */
import styled from '@emotion/styled';
import { useIntl } from 'react-intl';
import useClipboard from 'react-use-clipboard';

import { IconButton } from '@atlaskit/button/new';
import CopyIcon from '@atlaskit/icon/core/copy';
import SkipUnskipIcon from '@atlaskit/icon-lab/core/skip-unskip';
import { Flex } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';

import {
  BulkTestCaseActions,
  messages,
  TestLink,
  TestMetricValue,
  TestTableHeader,
  TestTextWrapper,
  TestTitle,
  TestManagementTable,
  type TestCases,
  TestSuiteName,
  TestSuitNameWrapper,
  TestCaseInlineActions,
  TestCaseAttribute,
  ExecutionStrategy,
} from 'src/components/pipelines/components/test-management';
import {
  formatTestDurationsMs,
  PAGE_LEN,
} from 'src/components/pipelines/components/test-management/utils';
import { useUpdateTestCaseStatus } from 'src/components/pipelines/hooks/use-test-management-actions';
import { getTestExecutionUrl } from 'src/components/pipelines/utils/links';
import { getPages } from 'src/components/pipelines/utils/pagination';
import { useStatsigGate } from 'src/contexts/feature-provider';
import { StatsigFeatureKeys } from 'src/features';

import { HeaderWithHelpIcon } from './HeaderWithHelpIcon';
import TestCaseTags from './TestCaseTags';
import { TestStateDropdown } from './TestStateDropdown';

const COPIED_STATE_TIMEOUT_MS = 3000; // 3 seconds

const TableWrapper = styled.div({
  'tr:hover .copy-button': {
    opacity: 1,
  },
});

const CopyButtonWrapper = styled.div({
  opacity: 0,
  transition: 'opacity 0.2s ease-in-out',
  flexShrink: 0,
});

const TooltipWrapper = styled.div({
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
});

const TextWithButtonWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: token('space.075'),
  maxWidth: '100%',
});

const IconWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  width: '16px',
  flexShrink: 0,
});

type CopyButtonProps = {
  testName: string;
  formatMessage: (message: any) => string;
};

const CopyButton = ({ testName, formatMessage }: CopyButtonProps) => {
  const [, setCopied] = useClipboard(testName);
  const [showCopiedState, setShowCopiedState] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        setShowCopiedState(false);
        setIsHovered(false);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleCopy = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setCopied();
    setShowCopiedState(true);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Reset to default state after the specified timeout
    timeoutRef.current = setTimeout(() => {
      setShowCopiedState(false);
      timeoutRef.current = null;
    }, COPIED_STATE_TIMEOUT_MS);
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    // Reset copied state when mouse leaves
    if (showCopiedState) {
      setShowCopiedState(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  };

  // Only show tooltip content when hovered or just copied
  const shouldShowTooltip = isHovered || showCopiedState;
  const tooltipContent = showCopiedState
    ? formatMessage(messages.copied)
    : formatMessage(messages.copyAsText);

  return (
    <CopyButtonWrapper className="copy-button">
      <Tooltip content={shouldShowTooltip ? tooltipContent : ''} position="top">
        <IconButton
          icon={iconProps => <CopyIcon {...iconProps} />}
          label={formatMessage(messages.copyTestName)}
          appearance="subtle"
          spacing="compact"
          onClick={handleCopy}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      </Tooltip>
    </CopyButtonWrapper>
  );
};

type Props = {
  testCases?: TestCases;
  isFixFlakyAvailable: boolean;
  isLoading?: boolean;
  reloadTestCases: () => void;
};

export const TestCaseTable = ({
  testCases,
  isFixFlakyAvailable,
  isLoading,
  reloadTestCases,
}: Props) => {
  const { formatMessage } = useIntl();
  const { updateTestCaseStatus, isTestCaseUpdating } =
    useUpdateTestCaseStatus(reloadTestCases);

  const isTestsBulkActionsEnabled = useStatsigGate(
    StatsigFeatureKeys.isTestsBulkActionsEnabled
  );
  const isTestsOpenBetaFeaturesEnabled = useStatsigGate(
    StatsigFeatureKeys.isTestsOpenBetaFeaturesEnabled
  );
  const isTestsFixFlakyActionEnabled = useStatsigGate(
    StatsigFeatureKeys.isTestsFixFlakyActionEnabled
  );
  const isTestsRemoveActionEnabled = useStatsigGate(
    StatsigFeatureKeys.isTestsRemoveActionEnabled
  );
  const isTestsCreateJiraWorkItemEnabled = useStatsigGate(
    StatsigFeatureKeys.isTestsCreateJiraWorkItemEnabled
  );

  const tableHead = useMemo(
    () => ({
      cells: [
        {
          key: 'test',
          content: (
            <TestTableHeader>{formatMessage(messages.test)}</TestTableHeader>
          ),
          width: 32,
        },
        {
          key: 'test.state',
          content: (
            <TestTableHeader>
              {formatMessage(messages.testState)}
            </TestTableHeader>
          ),
          width: 7,
        },
        {
          key: 'metrics.failureRate',
          content: (
            <TestTableHeader>
              <HeaderWithHelpIcon
                tooltipText={formatMessage(messages.failureRateTooltip)}
              >
                {formatMessage(messages.failureRate)}
              </HeaderWithHelpIcon>
            </TestTableHeader>
          ),
          width: 10,
          isSortable: true,
        },
        ...(isTestsOpenBetaFeaturesEnabled
          ? [
              {
                key: 'metrics.flakinessScore',
                content: (
                  <TestTableHeader>
                    <HeaderWithHelpIcon
                      tooltipText={formatMessage(messages.flakyScoreTooltip)}
                    >
                      {formatMessage(messages.flakinessScore)}
                    </HeaderWithHelpIcon>
                  </TestTableHeader>
                ),
                width: 11,
                isSortable: true,
              },
            ]
          : []),
        {
          key: 'metrics.averageDurationInMillis',
          content: (
            <TestTableHeader>
              <HeaderWithHelpIcon
                tooltipText={formatMessage(messages.averageDurationTooltip)}
              >
                {formatMessage(messages.averageDuration)}
              </HeaderWithHelpIcon>
            </TestTableHeader>
          ),
          width: 12,
          isSortable: true,
        },
        {
          key: 'metrics.varianceInMillis',
          content: (
            <TestTableHeader>
              <HeaderWithHelpIcon
                tooltipText={formatMessage(messages.varianceTooltip)}
              >
                {formatMessage(messages.variance)}
              </HeaderWithHelpIcon>
            </TestTableHeader>
          ),
          width: 8,
          isSortable: true,
        },
        {
          key: 'tags',
          content: (
            <TestTableHeader>{formatMessage(messages.tags)}</TestTableHeader>
          ),
          width: 15,
        },
        {
          key: 'actions',
          content: (
            <TestTableHeader>{formatMessage(messages.actions)}</TestTableHeader>
          ),
          width: 5,
        },
      ],
    }),
    [formatMessage, isTestsOpenBetaFeaturesEnabled]
  );

  const tableRows = useMemo(() => {
    return testCases?.values?.map(
      (
        {
          uuid,
          name,
          suiteName,
          metrics: {
            failureRate,
            flakinessScore,
            averageDurationInMillis,
            varianceInMillis,
          },
          customProperties,
          attributes = [],
          executionStrategy = ExecutionStrategy.DEFAULT,
        },
        index
      ) => {
        const rowKey = `row-${uuid}`;
        const isQuarantined =
          executionStrategy === ExecutionStrategy.QUARANTINED;
        return {
          key: rowKey,
          cells: [
            {
              key: `${rowKey}-test`,
              content: (
                <Flex direction="column">
                  <TestLink to={getTestExecutionUrl(uuid)}>
                    <TestTitle>
                      <IconWrapper>
                        {isQuarantined && (
                          <Tooltip
                            content={formatMessage(
                              messages.quarantinedTestSummary
                            )}
                          >
                            <SkipUnskipIcon
                              label={formatMessage(messages.quarantine)}
                              size="small"
                            />
                          </Tooltip>
                        )}
                      </IconWrapper>
                      <TextWithButtonWrapper>
                        <TooltipWrapper>
                          <Tooltip content={name}>
                            <TestTextWrapper>{name}</TestTextWrapper>
                          </Tooltip>
                        </TooltipWrapper>
                        <CopyButton
                          testName={name}
                          formatMessage={formatMessage}
                        />
                      </TextWithButtonWrapper>
                    </TestTitle>
                  </TestLink>
                  <TestSuiteName>
                    <IconWrapper />
                    <TooltipWrapper>
                      <Tooltip content={suiteName}>
                        <TestSuitNameWrapper>{suiteName}</TestSuitNameWrapper>
                      </Tooltip>
                    </TooltipWrapper>
                  </TestSuiteName>
                </Flex>
              ),
            },
            {
              key: `${rowKey}-testType`,
              content: (
                <TestStateDropdown
                  testUuid={uuid}
                  attributes={attributes as TestCaseAttribute[]}
                  isTestCaseUpdating={isTestCaseUpdating}
                  updateTestCaseStatus={updateTestCaseStatus}
                  formatMessage={formatMessage}
                />
              ),
            },
            {
              key: `${rowKey}-failureRate`,
              content: (
                <TestMetricValue hasWarning={failureRate > 30}>
                  {failureRate}%
                </TestMetricValue>
              ),
            },
            ...(isTestsOpenBetaFeaturesEnabled
              ? [
                  {
                    key: `${rowKey}-flakyScore`,
                    content: (
                      <TestMetricValue>
                        {flakinessScore === undefined ? '--' : flakinessScore}
                      </TestMetricValue>
                    ),
                  },
                ]
              : []),
            {
              key: `${rowKey}-averageDuration`,
              content: (
                <TestMetricValue>
                  {formatTestDurationsMs(averageDurationInMillis)}
                </TestMetricValue>
              ),
            },
            {
              key: `${rowKey}-variance`,
              content: (
                <TestMetricValue>
                  {formatTestDurationsMs(varianceInMillis)}
                </TestMetricValue>
              ),
            },
            {
              key: `${rowKey}-tags`,
              content: (
                <TestCaseTags
                  customProperties={customProperties}
                  index={index}
                />
              ),
            },
            {
              key: `${rowKey}-actions`,
              content: (
                <TestCaseInlineActions
                  testName={name}
                  testUuid={uuid}
                  attributes={attributes}
                  executionStrategy={executionStrategy}
                  isFixFlakyAvailable={isFixFlakyAvailable}
                  isTestCaseUpdating={isTestCaseUpdating}
                  isCreateJiraWorkItemEnabled={isTestsCreateJiraWorkItemEnabled}
                  isFixFlakyActionEnabled={isTestsFixFlakyActionEnabled}
                  isRemoveActionEnabled={isTestsRemoveActionEnabled}
                  updateTestCaseStatus={updateTestCaseStatus}
                  reloadTestCases={reloadTestCases}
                />
              ),
            },
          ],
        };
      }
    );
  }, [
    formatMessage,
    isFixFlakyAvailable,
    isTestCaseUpdating,
    isTestsCreateJiraWorkItemEnabled,
    isTestsFixFlakyActionEnabled,
    isTestsOpenBetaFeaturesEnabled,
    isTestsRemoveActionEnabled,
    reloadTestCases,
    testCases?.values,
    updateTestCaseStatus,
  ]);

  return (
    <TableWrapper>
      <TestManagementTable
        head={tableHead}
        rows={tableRows}
        pages={getPages(testCases?.size ?? 0, PAGE_LEN)}
        isLoading={isLoading || isTestCaseUpdating}
        multiSelect={
          isTestsBulkActionsEnabled
            ? {
                rowData: testCases?.values ?? [],
                selectionLabel: selectedCount =>
                  formatMessage(messages.selectedTestsCount, {
                    count: selectedCount,
                  }),
                clearSelectionLabel: formatMessage(messages.clearSelection),
                renderActions: ({ selectedItems, clearSelection }) => (
                  <BulkTestCaseActions
                    selectedTests={selectedItems}
                    onActionComplete={() => {
                      clearSelection();
                      reloadTestCases();
                    }}
                  />
                ),
              }
            : undefined
        }
      />
    </TableWrapper>
  );
};
